<template>
  <header class="relative mx-4 sm:mx-8 lg:mx-0 mb-8">
    <div
      class="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-8 sm:gap-y-6 items-center min-h-[70vh] md:h-75vh mt-6 sm:mt-8 md:mt-0">
      <div class="order-2 md:order-1 px-4 sm:px-6 md:px-0 md:w-full lg:w-4/5 md:ml-auto md:mr-8 lg:mr-16 space-y-6">
        <TitleH1 class="text-center md:text-left text-2xl sm:text-3xl lg:text-4xl"
          titleText="La Parka de surf personnalisée" />
        <Paragraph class="text-center md:text-left text-base sm:text-lg">
          S’engager à vos côtés pour promouvoir une pratique du surf inclusive et durable.
        </Paragraph>

        <div
          class="flex flex-col sm:flex-row md:flex-col lg:flex-row items-center justify-center md:justify-start gap-4 sm:gap-5">
          <Button textButton="Écrivez-nous" dark @click="openModal('contactForm')" class="w-full sm:w-auto" />
          <a href="https://app.jatago.fr/product-selection" target="_blank"
            class="w-full sm:w-auto rounded-full text-center font-bold transition-all duration-300 text-nowrap bg-white text-black hover:bg-olive hover:text-white hover:scale-105 px-8 py-4">Personnalisez
            en ligne</a>
        </div>

      </div>
      <div
        class="order-1 md:order-2 flex items-center justify-center md:justify-end h-80 sm:h-80 md:h-full overflow-hidden">
        <Slider :images="images" class="h-full w-full" />
      </div>
    </div>


    <!-- Modal Testimonial -->
    <Transition name="fade">
      <Modal v-if="modalOpen === 'videoTestimonial'" @close="closeModal">
        <template #content>
          <div class="flex flex-col gap-5 h-full justify-center">
            <video autoplay loop class="rounded-lg">
              <source src="@/assets/videos/interview.mp4" type="video/mp4">
              Votre navigateur ne supporte pas la lecture vidéo.
            </video>
            <div class="w-full">
              <LigthButton textButton="Contactez-moi" @click="openModal('contactForm')"></LigthButton>
            </div>
          </div>
        </template>
      </Modal>
    </Transition>

    <!-- Modal Contact -->
    <Transition name="fade">
      <Modal v-if="modalOpen === 'contactForm'" @close="closeModal">
        <template #content>
          <ContactForm :initialSizes="selectedSizes"></ContactForm>
        </template>
      </Modal>
    </Transition>
  </header>

  <section
    class="sm:bg-olive w-full sm:w-4/5 md:max-w-screen-xl px-4 sm:px-6 py-6 sm:py-8 mx-auto text-center lg:py-16 lg:px-6 rounded-none sm:rounded-xl my-6 sm:my-8">
    <SocialProofBanner />
  </section>

  <section
    class="max-w-screen-xl px-4 sm:px-6 py-6 sm:py-8 mx-auto text-center lg:py-16 lg:px-6 rounded-xl my-6 sm:my-8">
    <div class="max-w-screen-md mx-auto">
      <p class="uppercase mb-4 text-sm sm:text-base">Découvrez</p>
      <TitleH2 class="text-xl sm:text-2xl lg:text-3xl"
        titleText="Équipez votre club avec des parkas et ponchos personnalisés"></TitleH2>
    </div>

    <div class="my-12 sm:my-16 lg:my-20"></div>

    <SectionDetail sub-title="Un accompagnement sur mesure" title="Un suivi simple et efficace pour votre club"
      :image="require('@/assets/images/decoration/3.png')" flexReverse>
      <template #content>
        <p class="text-lg sm:text-base">
          Chaque club a des besoins
          différents : tailles, quantités, modèles, délais… Je vous accompagne du début à la fin pour vous proposer
          les équipements qui correspondent réellement à votre structure. Nous échangeons sur vos besoins, votre
          budget, vos contraintes, et je vous propose des solutions adaptées sans complications. L’objectif : que
          votre commande soit claire, rapide et conforme à vos attentes, du premier contact à la livraison.
        </p>
      </template>
    </SectionDetail>

    <div class="my-12 sm:my-16 lg:my-20"></div>

    <SectionDetail sub-title="Pas que des parkas : T-shirts, sweats et plus encore"
      title="Une gamme complète pour équiper vos membres" :image="require('@/assets/images/decoration/1.png')">

      <template #content>
        <p class="text-lg sm:text-base">
          En plus des parkas de surf, vous pouvez personnaliser tous les essentiels de votre club : T-shirts,
          sweats, vestes, ponchos, tote-bags…
          Des textiles pratiques, résistants et adaptés à la vie de club, avec des options de personnalisation simples
          (logo, couleurs, inscriptions).
          Une solution idéale pour harmoniser votre image lors des événements, compétitions ou sessions d’entraînement.
        </p>
      </template>
    </SectionDetail>

    <div class="my-12 sm:my-16 lg:my-20"></div>

    <SectionDetail sub-title="Des équipements pensés pour durer"
      title="Qualité, confort et robustesse pour un usage régulier" :image="require('@/assets/images/decoration/2.png')"
      flexReverse>

      <template #content>
        <p class="text-lg sm:text-base">
          <strong></strong> </br> Vos membres utilisent leur matériel au quotidien : vent, sel, pluie, transports… C’est
          pourquoi je sélectionne des produits conçus pour durer et résister aux conditions de bord de mer.
          Matériaux solides, finitions propres, impressions durables : tout est pensé pour offrir un équipement fiable,
          agréable à porter et adapté à la pratique du surf en club.
          Vous recevez des produits prêts à l’emploi, conformes à vos usages et à l’image que vous souhaitez
          transmettre.
        </p>
      </template>
    </SectionDetail>
  </section>

  <section
    class="bg-indigo-950 max-w-screen-xl sm:mx-auto px-4 sm:px-6 lg:px-6 py-6 sm:py-8 lg:py-16 rounded-none sm:rounded-xl mx-4 sm:mx-8 lg:mx-auto my-6 sm:my-8 relative">
    <img :src="require('@/assets/images/banner/banner.png')"
      class="absolute inset-0 w-full h-full opacity-40 sm:opacity-100 object-cover object-right sm:object-center rounded-none sm:rounded-xl"
      alt="Banner">
    <div class="relative z-10 text-center">
      <div class="space-y-4 sm:space-y-6 lg:space-y-8 px-4 sm:px-0">
        <TitleH2 class="text-xl sm:text-2xl md:text-3xl lg:text-4xl"
          titleText="Contactez-nous pour équiper votre club." dark></TitleH2>
        <div class="flex gap-2 items-center justify-center">
          <Button textButton="Parlons de votre projet" dark @click="openModal('contactForm')"></Button>
          <a href="https://app.jatago.fr/product-selection" target="_blank"
            class="rounded-full text-center font-bold transition-all duration-300 text-nowrap bg-white text-black hover:bg-olive hover:text-white hover:scale-105 px-8 py-4">Personnalisez
            en ligne</a>
        </div>
      </div>
    </div>
  </section>


  <SectionTestimonial></SectionTestimonial>

  <section
    class="max-w-screen-xl px-4 sm:px-6 py-6 sm:py-8 mx-auto text-center lg:py-16 lg:px-6 rounded-xl my-6 sm:my-8">
    <div class="max-w-screen-md mx-auto">
      <p class="uppercase mb-4 text-center text-sm sm:text-base">Expérience</p>
      <TitleH2 class="text-xl sm:text-2xl md:text-3xl lg:text-4xl text-center"
        titleText="Mon savoir-faire, vos moments gravés pour l'éternité">
      </TitleH2>
    </div>

    <div class="my-12 sm:my-16 lg:my-20"></div>

    <SectionPricingCards @reservation="handleReservation" @sizes-changed="updateSizes"></SectionPricingCards>
  </section>

  <section
    class="max-w-screen-xl px-4 sm:px-6 py-6 sm:py-8 mx-auto text-center lg:py-16 lg:px-6 rounded-xl my-6 sm:my-8">
    <ContactForm ref="contactForm" :initialSizes="selectedSizes"></ContactForm>
  </section>

  <DemandExpress />

</template>

<script>
import TitleH1 from '@/Components/Typography/Headings/TitleH1.vue';
import TitleH2 from '@/Components/Typography/Headings/TitleH2.vue';

import Paragraph from '@/Components/Typography/Body/Paragraph.vue';
import Button from '@/Components/Buttons/Button.vue';
import LigthButton from '@/Components/Buttons/LigthButton.vue';
import VideoButton from '@/Components/Buttons/VideoButton.vue';
import ReponseExpress from '@/Components/Section/ReponseExpress.vue';
import Modal from '@/Components/Modals/Modal.vue';
import Slider from '@/Components/Swiper/Slider.vue';
import SocialProofBanner from '@/Components/SocialProofs/Banner.vue';

import SectionDetail from '@/Components/Section/Detail.vue';
import SectionTestimonial from '@/Components/Section/Testimonial/Testimonial.vue';
import SectionPricingCards from '@/Components/Section/Price/PrincingCards.vue';

import DemandExpress from '@/Components/Section/PopUp/DemandExpress.vue';

import ContactForm from '@/Components/Form/ContactForm.vue';


export default {
  name: 'HomeView',
  components: {
    TitleH1,
    TitleH2,
    Paragraph,
    Button,
    LigthButton,
    VideoButton,
    Modal,
    Slider,
    SocialProofBanner,
    SectionDetail,
    SectionTestimonial,
    ContactForm,
    SectionPricingCards,
    ReponseExpress,
    DemandExpress,
  },
  data() {
    return {
      modalOpen: false,
      selectedSizes: {
        'S/M': 0,
        'L/XL': 0,
        '2XL/3XL': 0
      },
      images: [
        require('@/assets/images/slider/2/0.jpeg'),
        require('@/assets/images/slider/2/1.jpeg'),
        require('@/assets/images/slider/2/2.jpeg'),
        require('@/assets/images/slider/2/3.jpeg'),
        require('@/assets/images/slider/2/4.jpeg'),
        require('@/assets/images/slider/2/5.png'),
        require('@/assets/images/slider/2/6.jpeg'),
        require('@/assets/images/slider/2/7.jpeg'),
        require('@/assets/images/slider/2/8.jpeg'),
        require('@/assets/images/slider/2/9.png'),
      ],
    };
  },
  mounted() {
  },

  computed: {
  },

  methods: {
    openModal(modalName) {
      this.modalOpen = modalName;
    },
    closeModal() {
      this.modalOpen = false;
    },

    updateSizes(data) {
      // Mettre à jour les tailles en temps réel
      if (data && data.sizes) {
        this.selectedSizes = { ...data.sizes };
      }
    },

    handleReservation() {
      // Défiler vers le formulaire de contact
      this.$nextTick(() => {
        const contactFormElement = this.$refs.contactForm.$el;
        if (contactFormElement) {
          contactFormElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    }
  }
}
</script>

<style>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
